import { AxiosError } from 'axios';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useClickAway } from 'react-use';
import { useGetLoginUserInfo } from '../../../../api/auth/auth';
import { useGetManualTreeExcerptcolor, usePostManualTreeSearch } from '../../../../api/manual-tree/manual-tree';
import {
  ErrorInfo,
  ManualTreeExcerptColorResult,
  ManualTreeSearchConditions,
  Section,
  SectionPrivilage,
} from '../../../../api/model';
import { useDefaultOptions } from '../../../../hooks/api';
import useContextMenu from '../../../../hooks/useContextMenu';
import SpinnerModal from '../../../modules/SpinnerModal';
import { SectionMain } from './SectionMain';
import { SectionChapter } from './tree/SectionChapter';
import { SectionChapterMenuAdd } from './tree/chaptermenu/SectionChapterMenuAdd';
import './tree/contextmenu.css';

type Props = {
  productionId: number;
  sectionPrivilege: SectionPrivilage | undefined;
};

function SectionIndex({ productionId, sectionPrivilege }: Props) {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const options = useDefaultOptions(language);

  const [secReading, setSecReading] = useState<boolean>(false); // 読込み中

  const { data: excerptcolorData, refetch: excerptcolorRefetch } = useGetManualTreeExcerptcolor(productionId, options);
  const TreeExcerptColor: ManualTreeExcerptColorResult[] = excerptcolorData?.data || [];

  const { mutateAsync, data } = usePostManualTreeSearch<AxiosError<ErrorInfo>, unknown>(options);
  const [params] = useSearchParams();
  const [sectionId, setSectionId] = useState<number | null>(Number(params.get('section_id')) || null);

  // 初期のセクション
  const [initSection, setInitSection] = useState<Section>();
  const { register, getValues } = useForm<ManualTreeSearchConditions>({
    defaultValues: {
      production_id: Number(productionId),
      level: '',
      search_text: '',
    },
  });

  // 初期にパラメータから開く章
  const [initChapterId, setInitChapterId] = useState(0);

  const [allOpen, setAllOpen] = useState(false);
  const [allClose, setAllClose] = useState(false);

  /* eslint @typescript-eslint/no-explicit-any: off */
  const chapters: any = useMemo(() => data?.data.manual_tree_chapters || [], [data?.data.manual_tree_chapters]);
  const productionDivisionId = data?.data.production_division_id;

  const handleSearch = async () => {
    setAllOpen(false);
    setAllClose(false);
    await mutateAsync({
      data: { production_id: Number(productionId), level: getValues('level'), search_text: getValues('search_text') },
    });
  };

  const manualTreeSearch = useCallback(async () => {
    await mutateAsync({ data: { production_id: Number(productionId) } });
  }, [productionId, mutateAsync]);

  const defaultOptions = useDefaultOptions(language);
  const loginUserInfo = useGetLoginUserInfo(defaultOptions);
  const user = loginUserInfo.data?.data || {};
  const [beforeMovingMyEditSection, setBeforeMovingMyEditSection] = useState<boolean | null>(null);

  // 一覧表示
  useEffect(() => {
    void manualTreeSearch();
  }, [manualTreeSearch]);

  useEffect(() => {
    if (params.get('section_id')) {
      setSecReading(true);
      setSectionId(Number(params.get('section_id')));
    }
  }, [params, setSectionId, setSecReading]);

  useEffect(() => {
    if (initSection && initSection.level === 2 && initSection.chapter_id) {
      setInitChapterId(initSection.chapter_id);
    }
  }, [initSection]);

  const [isEdit, setIsEdit] = useState(false);
  const [contextOpen, setContextOpen] = useState(false);
  const { points, setPoints } = useContextMenu();
  const ref = useRef(null);
  useClickAway(ref, () => {
    setContextOpen(false);
  });

  const handleContextMenu = (e: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
    e.preventDefault();
    setContextOpen(true);
  };

  // サイドバーメニュー
  const isSidebarSticky = useRef(false);
  const initialSidebarTop = useRef(0);
  const setSidebarOverflow = () => {
    const element = document.getElementById('pslfr');
    const windowHeight = window.innerHeight;
    const sidebarHeight = windowHeight - 85;
    if (element) {
      element.style.maxHeight = `${sidebarHeight}px`;
      // element.style.overflow = 'auto';
    }
    const elementIn = document.getElementById('ori-scroll');
    if (elementIn) {
      const sidebarInHeight = windowHeight - 235;
      elementIn.style.maxHeight = `${sidebarInHeight}px`;
    }
  };
  const hideContentsOverflow = () => {
    const element = document.getElementById('#contents');
    if (element) {
      element.style.overflow = 'hidden';
    }
  };
  const handleSidebarSrcoll = () => {
    const element = document.getElementById('pslfr');
    if (element) {
      const scrollTop = window.scrollY;
      const windowHeight = window.innerHeight;
      const sidebarHeight = isSidebarSticky.current === true ? windowHeight - 20 : windowHeight - 85;
      if (scrollTop > initialSidebarTop.current) {
        const elementWidth = element.getBoundingClientRect().width;
        element.style.width = `${elementWidth}px`;
        element.style.position = 'fixed';
        element.style.top = '0';
        element.style.maxHeight = `${sidebarHeight}px`;
        isSidebarSticky.current = true;
      } else {
        element.style.position = 'static';
        isSidebarSticky.current = false;
        element.style.maxHeight = `${sidebarHeight}px`;
      }
    }
    const elementIn = document.getElementById('ori-scroll');
    if (elementIn) {
      const windowHeight = window.innerHeight;
      const sidebarInHeight = isSidebarSticky.current === true ? windowHeight - 160 : windowHeight - 225;
      elementIn.style.maxHeight = `${sidebarInHeight}px`;
    }
  };
  useEffect(() => {
    hideContentsOverflow();
    setSidebarOverflow();
    window.addEventListener('resize', setSidebarOverflow);
    initialSidebarTop.current = document.getElementById('pslfr')?.getBoundingClientRect().top || 0;
    window.addEventListener('scroll', handleSidebarSrcoll);
    const contentsElement = document.getElementById('contents');
    if (contentsElement) {
      contentsElement.addEventListener('scroll', (e: Event) => {
        const centerPanelElement = document.getElementById('product-manage-main2');
        if (centerPanelElement) {
          const { scrollLeft } = e.target as HTMLElement;
          if (scrollLeft > 0) {
            const sidebarElement = document.getElementById('pslfr');
            if (sidebarElement) {
              const centerPanelPaddingLeft = centerPanelElement.getBoundingClientRect().left;
              const sidebarWidth = sidebarElement.getBoundingClientRect().width;
              const left = centerPanelPaddingLeft - sidebarWidth;
              sidebarElement.style.left = `${left}px`;
            }
          }
        }
      });
    }
  }, []);

  return (
    <div id="product-manage">
      <div id="product-side-list">
        <div id="pslfr">
          <div className="productmenu">
            <form>
              <ul className="pm-form">
                <li>
                  <span>
                    <span className="dropdown">
                      <select {...register('level')}>
                        <option value="">{t('選択してください')}</option>
                        <option value="1">L1</option>
                        <option value="2">L2</option>
                        <option value="3">L3</option>
                      </select>
                    </span>
                  </span>
                </li>
                <li>
                  {' '}
                  <input type="text" {...register('search_text')} />
                </li>
                <li>
                  {' '}
                  <span>
                    <input
                      type="button"
                      id=""
                      name=""
                      value={t('検索').toString()}
                      className="searchbtn"
                      onClick={() => handleSearch()}
                    />
                  </span>
                </li>
              </ul>
            </form>

            {/* om追補, navi, 翻訳om追補, 翻訳navi */}
            <div id="ori-scroll" className="tree ori-tree">
              {productionDivisionId === 2 ||
              productionDivisionId === 3 ||
              productionDivisionId === 8 ||
              productionDivisionId === 9 ? (
                <span
                  onContextMenu={(event: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
                    event.preventDefault();
                    setPoints({ x: event.pageX, y: event.pageY });
                    handleContextMenu(event);
                  }}
                >
                  <h6
                    style={{ cursor: 'pointer' }}
                    onContextMenu={(event: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
                      event.preventDefault();
                      if (allOpen) {
                        setAllOpen(false);
                        setAllClose(true);
                      } else {
                        setAllOpen(true);
                        setAllClose(false);
                      }
                    }}
                  >
                    <i className="fa-solid fa-book" />
                    {t('マニュアルツリー')}
                  </h6>
                  <ul
                    className="popup"
                    style={{
                      cursor: 'pointer',
                      display: contextOpen ? 'block' : '',
                      position: 'fixed',
                      top: `${points.y}px`,
                      left: `${points.x}px`,
                      backgroundColor: '#ffffff',
                      boxShadow: '3px 2px 2px 1px rgba(211, 211, 211, 0.7)',
                      borderRadius: '4px',
                      zIndex: 20,
                      padding: '8px 8px 3px 8px',
                      marginBottom: '5px',
                    }}
                    ref={ref}
                  >
                    <SectionChapterMenuAdd productionId={productionId} />
                  </ul>
                </span>
              ) : (
                <h6
                  style={{ cursor: 'pointer' }}
                  onContextMenu={(event: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
                    event.preventDefault();
                    if (allOpen) {
                      // closeAllChapters();
                      setAllOpen(false);
                      setAllClose(true);
                    } else {
                      // openAllChapters();
                      setAllOpen(true);
                      setAllClose(false);
                    }
                  }}
                >
                  <i className="fa-solid fa-book" />
                  {t('マニュアルツリー')}
                </h6>
              )}
              <ul id="manual_tree">
                {/* eslint @typescript-eslint/no-explicit-any: off */}
                {/* eslint @typescript-eslint/no-unsafe-call: off */}
                {chapters.map(
                  (chapter: any) =>
                    productionDivisionId && (
                      <SectionChapter
                        isEdit={isEdit}
                        setIsEdit={setIsEdit}
                        productionId={productionId}
                        productionDivisionId={productionDivisionId}
                        sectionId={sectionId}
                        chapter={chapter}
                        user={user}
                        setSectionId={setSectionId}
                        key={`chapter-${String(chapter.chapter_id)}`}
                        onSearch={handleSearch}
                        beforeMovingMyEditSection={beforeMovingMyEditSection}
                        setBeforeMovingMyEditSection={setBeforeMovingMyEditSection}
                        initChapterId={initChapterId}
                        allOpen={allOpen}
                        allClose={allClose}
                        TreeExcerptColor={TreeExcerptColor}
                        excerptcolorRefetch={excerptcolorRefetch}
                        setSecReading={setSecReading}
                        secReading={secReading}
                      />
                    )
                )}
              </ul>
            </div>
          </div>
        </div>
      </div>
      {sectionId && (
        <SectionMain
          sectionId={sectionId}
          sectionPrivilege={sectionPrivilege}
          productionDivisionId={Number(productionDivisionId)}
          setInitSection={setInitSection}
          setIsEdit={setIsEdit}
          setSecReading={setSecReading}
        />
      )}
      {secReading && <SpinnerModal />}
    </div>
  );
}
export { SectionIndex };
